import { useRef, useState } from 'react'
import { Upload, X, Plus, GripVertical, ChevronDown, ChevronUp } from 'lucide-react'
import { uploadMedia } from '@/lib/storage'

// ─── Primitive fields ─────────────────────────────────────────────────────────

const base = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500'

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // eslint-disable-next-line jsx-a11y/label-has-for -- deprecated rule wants htmlFor+id too, but `children` here is an arbitrary control whose id isn't known generically; nesting alone already associates label and control correctly
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  )
}

export function TextField({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string
}) {
  return (
    <Field label={label}>
      <input aria-label={label} className={base} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
    </Field>
  )
}

export function TextareaField({ label, value, onChange, rows = 4, mono = false, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; rows?: number; mono?: boolean; placeholder?: string
}) {
  return (
    <Field label={label}>
      <textarea
        aria-label={label}
        className={`${base} resize-none ${mono ? 'font-mono text-xs' : ''}`}
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
      />
    </Field>
  )
}

export function SelectField<T extends string>({ label, value, onChange, options }: {
  label: string; value: T; onChange: (v: T) => void
  options: Array<{ value: T; label: string }>
}) {
  return (
    <Field label={label}>
      <select className={base} value={value} onChange={e => onChange(e.target.value as T)}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  )
}

export function NumberField({ label, value, onChange, min, max }: {
  label: string; value: number; onChange: (v: number) => void; min?: number; max?: number
}) {
  return (
    <Field label={label}>
      <input type="number" aria-label={label} className={base} value={value} min={min} max={max}
        onChange={e => onChange(Number(e.target.value))} />
    </Field>
  )
}

export function ColorField({ label, value, onChange }: {
  label: string; value: string; onChange: (v: string) => void
}) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input type="color" aria-label={label} value={value || '#ffffff'} onChange={e => onChange(e.target.value)}
          className="h-9 w-12 cursor-pointer rounded-lg border border-gray-200 p-0.5" />
        <input className={`${base} flex-1`} aria-label={`${label} (hex)`} value={value} onChange={e => onChange(e.target.value)}
          placeholder="#ffffff" />
      </div>
    </Field>
  )
}

export function ToggleField({ label, value, onChange }: {
  label: string; value: boolean; onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!value)}
        aria-label={label}
        aria-pressed={value}
        className={`relative h-5 w-9 rounded-full transition-colors ${value ? 'bg-violet-600' : 'bg-gray-200'}`}
      >
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </button>
    </div>
  )
}

// ─── Image field with upload ──────────────────────────────────────────────────

export function ImageField({ label, value, onChange, folder = 'uploads' }: {
  label: string; value: string; onChange: (v: string) => void; folder?: string
}) {
  const [uploading, setUploading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFile = async (file: File) => {
    setErr(null)
    setUploading(true)
    try {
      const url = await uploadMedia(file, folder)
      onChange(url)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const urlInputId = `image-field-url-${label.replace(/\W+/g, '-').toLowerCase()}`

  return (
    <div>
      {/* eslint-disable-next-line jsx-a11y/label-has-for -- htmlFor+id correctly ties this label to the URL input below; the deprecated rule also wants nesting, which the dropzone/preview layout in between makes impractical */}
      <label htmlFor={urlInputId} className="mb-1 block text-xs font-medium text-gray-600">{label}</label>

      {/* Preview */}
      {value && (
        <div className="relative mb-2 overflow-hidden rounded-lg border border-gray-200" style={{ aspectRatio: '16/9' }}>
          <img src={value} alt="" className="h-full w-full object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            aria-label="Remove image"
            className="absolute right-1.5 top-1.5 rounded-full bg-black/50 p-1 text-white hover:bg-black/70"
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* Upload button */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Click or drag to upload"
        className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-gray-200 py-3 transition-colors hover:border-violet-400 hover:bg-violet-50"
        onClick={() => inputRef.current?.click()}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click() } }}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f) }}
      >
        {uploading ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-violet-600 border-t-transparent" />
        ) : (
          <Upload size={14} className="text-gray-400" />
        )}
        <span className="text-xs text-gray-400">{uploading ? 'Uploading…' : 'Click or drag to upload'}</span>
        <input ref={inputRef} type="file" accept="image/*,video/*" aria-label={`Upload ${label}`} className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = '' }} />
      </div>

      {/* URL fallback */}
      <input id={urlInputId} aria-label={`${label} URL`} className={`${base} mt-2`} value={value} placeholder="Or paste URL…"
        onChange={e => onChange(e.target.value)} />

      {err && <p className="mt-1 text-xs text-red-500">{err}</p>}
    </div>
  )
}

// ─── Generic items array editor ───────────────────────────────────────────────

interface ItemsEditorProps<T extends Record<string, unknown>> {
  label: string
  items: T[]
  onChange: (items: T[]) => void
  defaultItem: T
  renderItem: (item: T, onChange: (patch: Partial<T>) => void, index: number) => React.ReactNode
  collapsed?: boolean
}

export function ItemsEditor<T extends Record<string, unknown>>({
  label, items, onChange, defaultItem, renderItem, collapsed = true,
}: ItemsEditorProps<T>) {
  const [openIndex, setOpenIndex] = useState<number | null>(collapsed ? null : 0)
  const [dragging, setDragging] = useState<number | null>(null)

  const update = (i: number, patch: Partial<T>) => {
    const next = [...items]
    next[i] = Object.assign({}, next[i], patch) as T
    onChange(next)
  }
  const remove = (i: number) => {
    onChange(items.filter((_, idx) => idx !== i))
    if (openIndex === i) setOpenIndex(null)
  }
  const add = () => {
    onChange([...items, { ...defaultItem }])
    setOpenIndex(items.length)
  }

  const dragStart = (i: number) => setDragging(i)
  const dragOver  = (e: React.DragEvent, i: number) => {
    e.preventDefault()
    if (dragging === null || dragging === i) return
    const next = [...items]
    const [moved] = next.splice(dragging, 1)
    next.splice(i, 0, moved!)
    onChange(next)
    setDragging(i)
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700">{label}</span>
        <button type="button" onClick={add}
          className="flex items-center gap-1 rounded-md bg-violet-50 px-2 py-1 text-xs font-medium text-violet-700 hover:bg-violet-100">
          <Plus size={11} /> Add
        </button>
      </div>

      <div className="space-y-1.5">
        {items.map((item, i) => (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- drag-to-reorder handle; the collapse toggle and remove buttons below remain independently keyboard-operable
          <div key={i} className="rounded-lg border border-gray-200 bg-white"
            draggable onDragStart={() => dragStart(i)} onDragOver={e => dragOver(e, i)} onDragEnd={() => setDragging(null)}>
            {/* Item header */}
            <div className="flex items-center gap-1.5 p-2">
              <GripVertical size={12} className="cursor-grab text-gray-300" />
              <button type="button" className="flex flex-1 items-center gap-1 text-left"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}>
                <span className="flex-1 truncate text-xs font-medium text-gray-700">
                  {(item.title as string) || (item.name as string) || (item.value as string) || `Item ${i + 1}`}
                </span>
                {openIndex === i ? <ChevronUp size={12} className="text-gray-400" /> : <ChevronDown size={12} className="text-gray-400" />}
              </button>
              <button type="button" onClick={() => remove(i)} aria-label="Remove item"
                className="rounded p-0.5 text-gray-300 hover:bg-red-50 hover:text-red-500">
                <X size={12} />
              </button>
            </div>

            {/* Item body */}
            {openIndex === i && (
              <div className="border-t border-gray-100 p-3 space-y-3">
                {renderItem(item, (patch) => update(i, patch), i)}
              </div>
            )}
          </div>
        ))}

        {items.length === 0 && (
          <p className="rounded-lg border border-dashed border-gray-200 py-4 text-center text-xs text-gray-400">
            No items yet — click Add
          </p>
        )}
      </div>
    </div>
  )
}

// ─── Section divider ──────────────────────────────────────────────────────────

export function Divider({ label }: { label?: string }) {
  if (!label) return <hr className="border-gray-100" />
  return (
    <div className="flex items-center gap-2">
      <hr className="flex-1 border-gray-100" />
      <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-300">{label}</span>
      <hr className="flex-1 border-gray-100" />
    </div>
  )
}
